import React, { useState } from "react";
import { toast } from "react-toastify";
import { X, Plus, Trash2, Clock } from "lucide-react";
import { Input } from "./ui/input";
import TimePickerModal from "./TimePickerModal";

interface TimeSlot {
  id: string;
  start_time: string;
  end_time: string;
}

interface ShiftModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (timeSlots: TimeSlot[]) => void;
  employeeName: string;
  date: Date;
  existingShifts?: TimeSlot[];
}

const ShiftModal: React.FC<ShiftModalProps> = ({
  isOpen,
  onClose,
  onSave,
  employeeName,
  date,
  existingShifts = [],
}) => {
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>(
    existingShifts.length > 0
      ? existingShifts
      : [{ id: Date.now().toString(), start_time: "", end_time: "" }]
  );
  const [loading, setLoading] = useState(false);
  const [activePicker, setActivePicker] = useState<{ id: string; field: "start_time" | "end_time" } | null>(null);

  // Format time for state (12h to 24h)
  const formatTimeTo24h = (time12: string): string => {
    if (!time12) return "";
    const match = time12.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return "";
    let [_, hours, minutes, period] = match;
    let h = parseInt(hours, 10);
    if (period.toLowerCase() === "pm" && h < 12) h += 12;
    if (period.toLowerCase() === "am" && h === 12) h = 0;
    return `${h.toString().padStart(2, "0")}:${minutes}`;
  };

  // Format time for display (24h to 12h)
  const formatTimeTo12h = (time24: string): string => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Theme colors
  const primaryPurple = "#885ABB";
  const darkPurple = "#402B69";
  const lightPurple = "#E5E7EB";
  const veryLightPurple = "#F9FAFB";

  // Format date for display
  const formatDateDisplay = (date: Date): string => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]}`;
  };

  // Handle add new time slot
  const handleAddTimeSlot = () => {
    setTimeSlots([
      ...timeSlots,
      { id: Date.now().toString(), start_time: "", end_time: "" },
    ]);
  };

  // Handle remove time slot
  const handleRemoveTimeSlot = (id: string) => {
    if (timeSlots.length > 1) {
      setTimeSlots(timeSlots.filter((slot) => slot.id !== id));
    }
  };

  // Handle time slot change
  const handleTimeSlotChange = (
    id: string,
    field: "start_time" | "end_time",
    value: string
  ) => {
    setTimeSlots(
      timeSlots.map((slot) =>
        slot.id === id ? { ...slot, [field]: value } : slot
      )
    );
  };

  // Handle save
  const handleSave = async () => {
    // Validate time slots
    const validSlots = timeSlots.filter(
      (slot) => slot.start_time && slot.end_time
    );

    if (validSlots.length === 0) {
      toast.error("Please add at least one valid time slot");
      return;
    }

    // Validate time ranges
    for (const slot of validSlots) {
      if (slot.start_time >= slot.end_time) {
        toast.error("End time must be after start time");
        return;
      }
    }

    setLoading(true);
    try {
      await onSave(validSlots);
      onClose();
    } catch (error) {
      console.error("Error saving shifts:", error);
    } finally {
      setLoading(false);
    }
  };

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      // Initialize with existing shifts or empty slot
      if (existingShifts.length > 0) {
        setTimeSlots(existingShifts);
      } else {
        setTimeSlots([{ id: Date.now().toString(), start_time: "", end_time: "" }]);
      }
    }
  }, [isOpen, existingShifts]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-md mx-4 border border-gray-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: lightPurple }}>
          <h2 className="text-lg font-semibold" style={{ color: darkPurple }}>
            {employeeName}'s {formatDateDisplay(date)}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" style={{ color: primaryPurple }} strokeWidth={2.5} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {timeSlots.map((slot, index) => (
            <div key={slot.id} className="space-y-3">
              <div className="flex items-end gap-3">
                {/* Start Time */}
                <div className="flex-1">
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: darkPurple }}
                  >
                    Start Time
                  </label>
                  <div
                    onClick={() => setActivePicker({ id: slot.id, field: "start_time" })}
                    className="flex items-center justify-between w-full px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-sm">
                      {slot.start_time ? formatTimeTo12h(slot.start_time) : "Select time"}
                    </span>
                    <Clock className="w-4 h-4 text-gray-400" />
                  </div>
                </div>

                {/* End Time */}
                <div className="flex-1">
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: darkPurple }}
                  >
                    End Time
                  </label>
                  <div
                    onClick={() => setActivePicker({ id: slot.id, field: "end_time" })}
                    className="flex items-center justify-between w-full px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-sm">
                      {slot.end_time ? formatTimeTo12h(slot.end_time) : "Select time"}
                    </span>
                    <Clock className="w-4 h-4 text-gray-400" />
                  </div>
                </div>

                {/* Add/Remove Buttons */}
                <div className="flex gap-2">
                  {timeSlots.length > 1 && (
                    <button
                      onClick={() => handleRemoveTimeSlot(slot.id)}
                      className="p-2 rounded-md border hover:bg-red-50 transition-colors"
                      style={{ borderColor: lightPurple }}
                      aria-label="Remove time slot"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  )}
                  {index === timeSlots.length - 1 && (
                    <button
                      onClick={handleAddTimeSlot}
                      className="p-2 rounded-md border hover:opacity-90 transition-colors"
                      style={{
                        borderColor: primaryPurple,
                        backgroundColor: veryLightPurple,
                      }}
                      aria-label="Add time slot"
                    >
                      <Plus className="w-4 h-4" style={{ color: primaryPurple }} strokeWidth={3} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50" style={{ borderColor: lightPurple }}>
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-md text-white font-medium transition-colors disabled:opacity-50"
            style={{ backgroundColor: "#EF4444" }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 rounded-md text-white font-medium transition-colors disabled:opacity-50 hover:opacity-90"
            style={{ backgroundColor: primaryPurple }}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <TimePickerModal
        isOpen={!!activePicker}
        onClose={() => setActivePicker(null)}
        selectedTime={
          activePicker
            ? formatTimeTo12h(timeSlots.find(s => s.id === activePicker.id)?.[activePicker.field] || "")
            : ""
        }
        onSelectTime={(time12) => {
          if (activePicker) {
            handleTimeSlotChange(activePicker.id, activePicker.field, formatTimeTo24h(time12));
          }
        }}
      />
    </div>
  );
};

export default ShiftModal;
